// audioUploadPersistence.js
//
// IndexedDB-backed persistence for in-flight audio uploads. Survives page
// close, browser crash, or network loss — the blob stays in IndexedDB until
// the server confirms the upload. On any subsequent page load, the recovery
// effect (in ReadingStyle10) re-reads pending entries and retries them in the
// background, silently — without disrupting whatever the user is doing.
//
// One row per upload, keyed by a caller-provided id (e.g. user+page+ts).
// Metadata mirrors the FormData fields used by /api/student/audio so the
// retry path can reconstruct the request without any extra context.

const DB_NAME = "BoosterAudioUploads";
const STORE = "pending";
const DB_VERSION = 1;

const open = () => new Promise((resolve, reject) => {
  if (typeof indexedDB === "undefined") {
    reject(new Error("IndexedDB unavailable"));
    return;
  }
  const req = indexedDB.open(DB_NAME, DB_VERSION);
  req.onupgradeneeded = () => {
    if (!req.result.objectStoreNames.contains(STORE)) {
      req.result.createObjectStore(STORE, { keyPath: "id" });
    }
  };
  req.onsuccess = () => resolve(req.result);
  req.onerror = () => reject(req.error);
});

// Persist a blob + metadata under `id`. Resolves on commit; throws on quota
// exhaustion or IDB unavailability. Callers should treat persistence as
// best-effort (e.g. private browsing mode may disable IDB entirely).
export const savePendingUpload = async (id, blob, metadata) => {
  if (!id || !blob) return;
  const db = await open();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE, "readwrite");
    tx.objectStore(STORE).put({
      id,
      blob,
      metadata: metadata || {},
      createdAt: Date.now()
    });
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
};

export const removePendingUpload = async (id) => {
  if (!id) return;
  const db = await open();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE, "readwrite");
    tx.objectStore(STORE).delete(id);
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
};

// List all pending entries. If `userId` provided, filters to that user only —
// supports shared-device cases where multiple students sign in on the same tab.
export const getPendingUploads = async (userId) => {
  const db = await open();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE, "readonly");
    const req = tx.objectStore(STORE).getAll();
    req.onsuccess = () => {
      const all = req.result || [];
      resolve(userId ? all.filter((r) => r.metadata && r.metadata.userId === userId) : all);
    };
    req.onerror = () => reject(req.error);
  });
};

// Increment the persisted retry counter for an entry. Callers use this to
// cap how many times the cross-session sweep re-attempts a single upload
// before giving up — without this, a permanently-rejecting upload (deleted
// page, schema change, etc.) re-fires on every mount forever.
export const incrementAttempts = async (id) => {
  if (!id) return 0;
  const db = await open();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE, "readwrite");
    const store = tx.objectStore(STORE);
    const getReq = store.get(id);
    getReq.onsuccess = () => {
      const row = getReq.result;
      if (!row) {
        resolve(0);
        return;
      }
      row.attempts = (row.attempts || 0) + 1;
      row.lastAttemptAt = Date.now();
      store.put(row);
    };
    tx.oncomplete = () => {
      const after = getReq.result;
      resolve(after ? (after.attempts || 0) : 0);
    };
    tx.onerror = () => reject(tx.error);
  });
};

// Max times the cross-session sweep retries a single entry before purging it.
// 15 chosen so a user with a flaky network pattern (opening the page multiple
// times during an outage) doesn't burn through the budget on a single bad day —
// each mount counts as one attempt. The 7-day cleanupOldUploads TTL is the
// outer bound; this counter just prevents hard-failure entries from being
// retried forever. 4xx responses are still purged immediately regardless.
export const MAX_RETRY_ATTEMPTS = 15;

// Purge entries older than `maxAgeMs` (default 7 days) OR that have hit
// MAX_RETRY_ATTEMPTS. Prevents the store from growing without bound when an
// upload keeps failing (server down, changed schema, etc.).
export const cleanupOldUploads = async (maxAgeMs = 7 * 24 * 3600 * 1000) => {
  let all;
  try {
    all = await getPendingUploads();
  } catch (e) {
    return;
  }
  const now = Date.now();
  for (const r of all) {
    const tooOld = now - r.createdAt > maxAgeMs;
    const exhausted = (r.attempts || 0) >= MAX_RETRY_ATTEMPTS;
    if (tooOld || exhausted) {
      try { await removePendingUpload(r.id); } catch (e) {}
    }
  }
};
